﻿import AppointmentList from "./appointment-list";
import AppointmentCard from "./appointment-card";
import AppointmentsService from "./appointments-service";
import BlockUI from "./block-ui";
import LoginModal from "./login-modal";
import PaymentModal from "./payment-modal";
import MessagePoster from "./message-poster";
import TermsModal from "./terms-modal";
import { AnimatedBorder } from "./animated-border";

export default class Schedule {
  #theme;
  #practiceTimezone;
  #appointmentList;
  #appointmentCard;
  #ui;

  #selectedProvider;
  #selectedTreatment;
  #selectedDate;
  #searchingMsg;

  #defaultSelectAllProviders;

  #animBorder;

  constructor(options) {
    this.#theme = options.theme;
    this.#practiceTimezone = options.practiceTimezone;
    this.#selectedDate = moment.tz(this.#practiceTimezone).startOf("day"); // Today, 00:00:00, retaining timezone.
    this.#defaultSelectAllProviders = options.defaultSelectAllProviders;
    this.#appointmentList = new AppointmentList();
    this.#appointmentCard = new AppointmentCard();
    this.#animBorder = new AnimatedBorder();

    this.#selectedProvider = "";
    this.#selectedTreatment = "";
    this.#searchingMsg = "Searching...<br>Please wait...";
  }

  async init() {
    this.#setCsrfToken();
    this.#checkTimezone();
    this.#initSelectElements();
    this.#cacheUiElements();

    this.#appointmentList.init(this.#practiceTimezone);
    this.#appointmentCard.init(this.#practiceTimezone);

    this.#bindEvents();

    // Messages are attached to the schedule element itself 
    // as the Book page is a SPA and the body is not refreshed.
    window.displayViewMessages($("#schedule").attr("data-messages"));

    this.#highlightElem(this.#ui.treatment.select2);
  }

  async #checkTimezone() {
    const deviceTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (this.#practiceTimezone != deviceTz) {
      $(".timezone-msg-wrap").removeClass("hidden");
    }
  }

  #setCsrfToken() {
    const csrfToken = $("#csrf-token").attr("data-token");
    AppointmentsService.setCsrfToken(csrfToken);
  }

  #initSelectElements() {
    $("#treatment-select").select2({
      forceDropdownPosition: "below",
      selectionCssClass: `theme--${this.#theme}`,
      dropdownCssClass: `theme--${this.#theme}`,
      minimumResultsForSearch: Infinity,
      templateSelection: this.#formatTreatmentSelection.bind(this),
      templateResult: this.#formatTreatmentDropdown.bind(this),
      width: "style",
      placeholder: "Select treatment from list",
    });

    $("#provider-select").select2({
      forceDropdownPosition: "below",
      selectionCssClass: `theme--${this.#theme}`,
      dropdownCssClass: `provider theme--${this.#theme}`,
      minimumResultsForSearch: Infinity,
      templateResult: this.#formatProviderDropdown.bind(this),
      width: "style",
      placeholder: "Select provider from list",
    });

    $(".select2-container").addClass("form-select");
  }

  #cacheUiElements() {
    this.#ui = {
      content: $("#content"),
      navbar: $("#nav-wrap"),
      provider: {
        wrap: $("#select-provider-wrap"),
        select: $("#provider-select"),
        select2: $("#select-provider-wrap .select2"),
        options: $("#provider-select option:not(:disabled)"),
      },
      treatment: {
        wrap: $("#select-treatment-wrap"),
        select: $("#treatment-select"),
        select2: $("#select-treatment-wrap .select2"),
      },
      appointment: {
        datePicker: $("#earliest-date-picker"),
        list: $("#select-appointment-wrap .card"),
        confirm: $("#appointment-selected")
      },
    };
  }

  #bindEvents() {
    // Treatment selection handler.
    this.#ui.treatment.select.change(async () => {
      this.#ui.provider.wrap.removeClass("hidden");
      this.#selectTreatment();

      this.#appointmentList.clear();
      this.#appointmentCard.deselect();
      this.#selectedDate = this.#appointmentList.selectedDate;

      window.scroller.scrollToElemTop(this.#ui.provider.wrap);
      this.#highlightElem(this.#ui.provider.select2);

      this.#preselectDefaultProvider();
    });

    // Provider selection handler.
    this.#ui.provider.select.change(async () => {
      this.#appointmentList.clear();
      this.#appointmentCard.deselect();
      this.#selectedDate = this.#appointmentList.selectedDate;
      this.#selectProvider();
    });

    // Clicking anywhere in the selection wrap should open the dropdown.
    $(".select-wrap").on("click", (e) => {
      e.stopPropagation();
      const $this = $(e.currentTarget);
      $this.find("select").select2("open");
    });

    // Resize the window when select elements are clicked to ensure they are not hidden.
    $("select").on("select2:open", (e) => {
      const $select = $(e.currentTarget);

      // If the select2 sibling is currently animated, pause the animation
      if ($select.siblings(".anim-border").length > 0) {
        this.#animBorder.pause();
      }

      const $dropdown = $select.data("select2").$results;
      window.resizer.fitElem($dropdown);
    });

    // Resize the window when select elements are closed to remove empty space
    $("select").on("select2:close", (e) => {
      const $select = $(e.currentTarget);

      // If the select2 sibling is currently animated, resume the animation
      if ($select.siblings(".anim-border").length > 0) {
        this.#animBorder.play();
      }

      window.resizer.fitElem($("body"));
    });

    // Get appointments when appointments list navigated.
    this.#appointmentList.eventTarget.on("change.appointment-list", async (e, date) => {
      this.#selectedDate = date;
      this.#getAppointments();
    });

    // Update appointment card when appointment selected from list.
    this.#appointmentList.eventTarget.on("select.appointment-list", (e, appointment) => {
      this.#appointmentCard.select(appointment);
      this.#highlightElem(this.#ui.appointment.confirm);
    });

    // Confirm the selected appointment on confirm clicked.
    this.#appointmentCard.eventTarget.on("confirm.appointment-card", (e, details) => {
      this.#bookAppointment(details)
    });
  }

  async #getAppointments() {
    if (this.#selectedTreatment === "" || this.#selectedProvider === "") {
      return;
    }

    BlockUI.blockWithId("appointment-details-card", this.#searchingMsg);

    this.#appointmentCard.deselect();

    let data;
    try {
      data = await AppointmentsService.getAppointments(
        this.#selectedProvider,
        this.#selectedTreatment,
        this.#selectedDate,
        window.customerConfig.toQueryString(),
      );
    } catch (errResp) {
      this.#handleGetAppointmentsRequestError(errResp);
      return;
    } finally {
      BlockUI.unblockWithId("appointment-details-card");
    }

    this.#appointmentList.show(data);
    this.#highlightElem(this.#ui.appointment.list);
  }

  async #bookAppointment({ id, deposit, note }) {
    BlockUI.blockWithId("schedule", "Working...<br>Please wait...");

    try {
      // Handle booking terms acceptance
      try {
        await this.#requestBookingTermsAcceptance(id);
      } catch (error) {
        this.#handleBookingTermsError(error);
        return;
      }

      // Start the booking process
      let bookingStarted = false;
      try {
        bookingStarted = await this.#beginBooking(id, note);
      } catch (error) {
        this.#handleBeginBookingError(error);
        return;
      }

      // If the booking didn't start then exit here,
      // any error should have been handled already
      if (!bookingStarted) {
        return;
      }

      // Handle deposit payment
      try {
        await this.#requestDepositIfNeeded(deposit);
      } catch (error) {
        this.#handleRequestDepositError(error);
        return;
      }

      // Handle appointment booking
      let data;
      try {
        data = await this.#confirmAppointment();
      } catch (errResp) {
        await this.#handleConfirmAppointmentRequestError(errResp, { id, note, note });
        return;
      }

      if (data) {
        // The appointment is booked.

        // Post booked message to parent
        MessagePoster.postBookingStatusBooked(data);

        // Display the booking confirmation
        window.customerConfig.returnUrl = "/Book";
        window.pageRouter.load(`/Book/Confirmation?${window.customerConfig.toQueryString()}`);
      }

    }
    finally {
      BlockUI.unblockWithId("schedule");
    }
  }

  #scrollToContent() {
    window.scroller.scrollToElemTop(this.#ui.content);
  }

  #highlightElem($elem) {
    // Wait for the next frame to ensure any dom changes have been redrawn
    setTimeout(() => {
      this.#animBorder.remove();
      this.#animBorder.attach($elem);
    }, 1);
  }

  // eslint-disable-next-line class-methods-use-this
  #formatProviderDropdown(provider) {
    if (!provider.id) {
      return provider.text;
    }

    const baseUrl = "/Image/Provider";

    let cleanProvider = DOMPurify.sanitize(provider.id);
    const cleanVersion = DOMPurify.sanitize($(provider.element).attr("data-photo-v"));
    const cleanText = DOMPurify.sanitize(provider.text);

    const rawTitle = $(provider.element).attr("data-professional-title");
    let cleanTitle = DOMPurify.sanitize(rawTitle);

    if (!cleanTitle) {
      const rawType = $(provider.element).attr("data-type");
      cleanTitle = DOMPurify.sanitize(rawType);

      // We don't need to show the `Any` type
      if (cleanTitle === "Any") {
        cleanTitle = "";
      }
      // Add `Dental` before `Therapist` for clarity
      else if (cleanTitle === "Therapist") {
        cleanTitle = "Dental Therapist";
      }
    }

    const $provider = $(
      `<div class="provider d-flex align-items-center">
      <img src="" class="thumbnail" />
      <div>
      <div class="name">${cleanText}</div>
      <div class="title">${cleanTitle}</div>
      </div>
      </div>`,
    );

    const $img = $provider.find("img");

    // If provider is not a specific id ("any") request the "any" image.
    // eslint-disable-next-line no-restricted-globals
    if (isNaN(cleanProvider)) {
      cleanProvider = "any";
    }

    let src = `${baseUrl}/${cleanProvider}`;
    if (cleanVersion) {
      src += `?v=${cleanVersion}`;
    }

    $img.attr("src", src);
    if (cleanProvider === "any") {
      $img.addClass("small");
    }

    return $provider;
  }

  #formatTreatmentSelection(treatment) {
    if (!treatment.id) {
      return treatment.text;
    }

    const $treatment = this.#getTreatmentSelectionElement(treatment);
    $treatment.addClass("selection");
    return $treatment;
  }

  #formatTreatmentDropdown(treatment) {
    if (!treatment.id) {
      return treatment.text;
    }

    const rawAdditional = $(treatment.element).attr("data-additional");
    const cleanAdditional = DOMPurify.sanitize(rawAdditional);

    const $treatment = this.#getTreatmentSelectionElement(treatment);

    const $additional = $(
      `<div class="add w-100">${cleanAdditional}</div>`
    );

    $treatment.append($additional)
      .addClass("results");

    return $treatment;
  }

  #getTreatmentSelectionElement(treatment) {
    const $treatmentOption = $(treatment.element);

    const rawDuration = $treatmentOption.attr("data-duration");
    const rawPrice = $treatmentOption.attr("data-price");
    const rawDiscount = $treatmentOption.attr("data-discount-price");
    const rawDeposit = $treatmentOption.attr("data-deposit");

    const cleanText = DOMPurify.sanitize(treatment.text);
    const cleanDuration = DOMPurify.sanitize(rawDuration);
    const cleanPrice = DOMPurify.sanitize(rawPrice);
    const cleanDiscount = DOMPurify.sanitize(rawDiscount);
    const cleanDeposit = DOMPurify.sanitize(rawDeposit);

    const $treatment = $(
      `<div class="treatment d-flex flex-wrap justify-content-between align-items-baseline">
      <span class="type flex-grow-1">${cleanText}</span>
      </div>`,
    );
    const $tags = $(
      `<span class="d-flex tags flex-wrap justify-content-start mr-auto"></span>`
    );

    if (cleanDiscount && cleanPrice) {
      $tags.append(`<span class="price tag">` +
        `<span class="sr-only">Price:&nbsp;</span>${cleanDiscount}&nbsp;` +
        `<span class="old-price">Was:&nbsp<span class="text-decoration-line-through">${cleanPrice}</span>` +
        `</span></span>`);
    }
    else if (cleanPrice) {
      $tags.append(`<span class="price tag">${cleanPrice}</span>`);
    }

    if (cleanDeposit) {
      $tags.append(`<span class="deposit tag">Deposit:&nbsp;${cleanDeposit}</span>`);
    }

    if (cleanDuration) {
      $tags.append(`<span class="tag">${cleanDuration}&nbsp;mins</span>`);
    }

    $treatment.append($tags);

    return $treatment;
  }

  #preselectDefaultProvider() {
    const $providers = this.#ui.provider.select.find("option").not(":disabled");

    // Select first provider if they are the one.
    if ($providers.length === 1) {
      this.#ui.provider.select.val($providers.first().val());
      this.#ui.provider.select.trigger("change");
    }
    // Otherwise select the "any" provider option if configured to do so
    else if (this.#defaultSelectAllProviders) {
      this.#ui.provider.select.val("any");
      this.#ui.provider.select.trigger("change");
    }

    // Otherwise don't preselect an option
  }

  #selectProvider() {
    this.#selectedProvider = this.#ui.provider.select.find("option:selected").val() ?? "";
    if (this.#selectedProvider) {
      this.#getAppointments();
    }
  }

  #selectTreatment() {
    const $treatmentOption = this.#ui.treatment.select.find("option:selected").first();
    this.#selectedTreatment = $treatmentOption.val() ?? "";
    const type = $treatmentOption.attr("data-type");
    this.#updateProviders(type);
  }

  #updateProviders(treatmentType) {
    // treatmentType is a comma separated list of compatible provider types
    let compatibleTypes = treatmentType.split(",");
    compatibleTypes = compatibleTypes.map(t => t.trim());

    // Enable providers with a matching type
    let count = 0;
    this.#ui.provider.options.each((index, elem) => {
      const $this = $(elem);
      const providerType = $this.attr("data-type");
      const professionalTitle = $this.attr("data-professional-title");
      const enabled = providerType === "Any" || compatibleTypes.includes("Any") || compatibleTypes.includes(providerType) || compatibleTypes.includes(professionalTitle);

      $this.attr("disabled", !enabled);
      $this.attr("hidden", !enabled);

      if (enabled && providerType !== "Any") {
        count++;
      }
    });

    // Hide "Any" option if less than two providers match
    if (count < 2) {
      const $any = this.#ui.provider.options.filter("option[value='any']");
      $any.attr("disabled", true);
      $any.attr("hidden", true);
    }

    // Reset the selection
    this.#ui.provider.select.val("");
    this.#ui.provider.select.trigger("change");
  }

  async #requestBookingTermsAcceptance(id) {
    window.pageRouter.pauseLinkInterceptor();

    const modal = new TermsModal();
    modal.init();

    try {
      await modal.requestAcceptance(id);
    } catch (error) {
      let message = "";
      if (error.message === "Booking terms closed.") {
        message = error.message;
      } else {
        console.error(error);
        message = "There was an error when trying to process your acceptance of the booking terms. Please try again.";
      }

      throw new Error(message);
    } finally {
      modal.close();
      window.pageRouter.resumeLinkInterceptor();
    }
  }

  /**
   * #beginBooking starts a new booking and returns true if it succeeds, otherwise it returns false;
   */
  async #beginBooking(appointmentId, bookingNote) {
    try {
      await AppointmentsService.beginBooking(appointmentId, bookingNote);
      return true;
    } catch (errResp) {
      switch (errResp.status) {
        // Unauthorized 
        case 401:
          // Retry after logging in
          await this.#requestLogin();
          return await this.#beginBooking(appointmentId, bookingNote);

        // Redirect
        case 302:
          // If we are redirected replace the current content so we don't lose
          // our session when cookies are blocked.
          if (errResp.responseJSON.location.toLowerCase().startsWith("/book")) {
            await window.pageRouter.load(errResp.responseJSON.location);
            return false;
          }

          // Otherwise this is an unexpected redirect - don't follow it as we don't want to lose
          // our session if cookies are blocked.
          throw new Error(`Unexpected redirect to: ${errResp.responseJSON.location}. Redirect ignored.`);

        default:
          if ("responseText" in errResp
            && (errResp.responseText.includes("Booking limit reached")
              || errResp.responseText.includes("not currently accepting appointment bookings from you"))
          ) {
            throw new Error(errResp.responseText);
          }

          throw new Error(`Could not create new booking. ${errResp.responseText}.`);
      }
    }
  }

  async #requestDepositIfNeeded(deposit) {
    if (deposit > 0) {
      // Request the payment from the user
      const paymentDetails = await this.#getPaymentDetails();
      if (paymentDetails) {
        await this.#requestPayment(paymentDetails);
      }
    }
  }

  async #requestPayment(paymentDetails) {
    const url = `${paymentDetails.gatewayUrl}?token=${paymentDetails.token}`;
    const modal = new PaymentModal(url);

    try {
      await modal.requestPayment();
    } catch (error) {
      let message = "";
      if (error.message === "Payment cancelled.") {
        message = error.message;
      } else {
        console.error(error);
        message = "Could not process your payment, please try again.";
      }

      throw new Error(message);
    } finally {
      modal.close();
    }

    // Payment success
  }

  async #requestLogin() {
    window.pageRouter.pauseLinkInterceptor();

    const modal = new LoginModal();
    modal.init();

    try {
      await modal.requestLogin();
    } catch (error) {
      let message = "";
      if (error.message === "Login cancelled.") {
        message = error.message;
      } else {
        console.error(error);
        message = "Could not log you in, please try again.";
      }

      throw new Error(message);
    } finally {
      modal.close();
      window.pageRouter.resumeLinkInterceptor();
    }

    // Login success
    await window.pageRouter.refreshNavbar();
  }

  async #getPaymentDetails() {
    let paymentDetails;

    try {
      paymentDetails = await AppointmentsService.getPaymentDetails();
    } catch (errResp) {
      switch (errResp.status) {
        // Unauthorized 
        case 401:
          // Retry after logging in
          await this.#requestLogin();
          return await this.#getPaymentDetails();

        // Forbidden
        case 403:
          if ("responseText" in errResp
            && (errResp.responseText.includes("maximum number of appointments booked")
              || errResp.responseText.includes("not currently accepting appointment bookings from you"))
          ) {
            throw new Error(errResp.responseText);
          }
      }

      // Otherwise throw a generic payment error
      throw new Error(`Could not get payment details. ${errResp.responseText}`);
    }

    return paymentDetails;
  }

  async #confirmAppointment() {
    try {
      return await AppointmentsService.confirmAppointment();
    } catch (errResp) {
      switch (errResp.status) {
        // Unauthorized 
        case 401:
          // Retry after logging in
          await this.#requestLogin();
          return await this.#confirmAppointment();

        // Forbidden
        case 403:
          if ("responseText" in errResp
            && (errResp.responseText.includes("maximum number of appointments booked")
              || errResp.responseText.includes("not currently accepting appointment bookings from you"))
          ) {
            throw new Error(errResp.responseText);
          }

        // Gone
        case 410: {
          throw new Error("Your chosen appointment time is no longer available. Please choose another time.");
        }

        // Aborted
        case 0: {
          throw new Error("The server could not be reached. Please try again later.");
        }

        // Redirect
        case 302: {
          // If we are redirected replace the current content so we don't lose
          // our session when cookies are blocked.
          if (errResp.responseJSON.location.toLowerCase().startsWith("/book")) {
            await window.pageRouter.load(errResp.responseJSON.location);

            // The booking wasn't confirmed;
            return null;
          }

          // Otherwise this is an unexpected redirect - don't follow it as we don't want to lose
          // our session if cookies are blocked.
          throw new Error(`Unexpected redirect to: ${errResp.responseJSON.location}. Redirect ignored.`);
        }

        default: {
          if ("responseText" in errResp) {
            // Handle booking session errors
            if (errResp.responseText.includes("Booking session timed out")
              || errResp.responseText.includes("Booking terms must be accepted")) {

              this.#handleBookingSessionError(errResp.responseText);
              break;
            }
          }

          throw new Error(`Could not create new booking. ${errResp.responseText}`);
        }
      }
    }
  }

  async #handleGetAppointmentsRequestError(errResp) {
    switch (errResp.status) {
      // Aborted
      case 0: {
        bootbox.alert({
          message: "The server could not be reached. Please try again later.",
          backdrop: true,
        });
        break;
      }

      default: {
        if ("responseText" in errResp
          && (errResp.responseText.includes("Booking session timed out")
            || errResp.responseText.includes("Booking terms must be accepted"))
        ) {
          this.#handleBookingSessionError(errResp.responseText);
          break;
        }

        console.error(errResp.responseText);
        this.#appointmentList.clear();

        const msg = "There was an error requesting available appointments. Please try again.";

        bootbox.alert({
          message: msg,
          backdrop: true,
        });
      }
    }
  }


  async #handleConfirmAppointmentRequestError(error) {
    console.error(error);

    if (error.message.includes("Booking session timed out")
      || error.message.includes("Booking terms must be accepted")) {
      this.#handleBookingSessionError(error.message);
      return;
    }

    if (error.message.includes("appointment time is no longer available")) {
      bootbox.alert({
        message: error.message,
        backdrop: true,
      });

      // Refresh the appointments list
      this.#getAppointments()
      return;
    }

    let message;
    if (error.message.includes("maximum number of appointments booked")
      || error.message.includes("not currently accepting appointment bookings from you")) {
      message = error.message;
    } else if (error.message === "Login cancelled.") {
      message = "Login cancelled. Please log in or register to book your appointment.";
    } else if (error.message === "The server could not be reached.") {
      message = "The server could not be reached. Please try again later.";
    } else {
      message = "There was a problem booking your appointment. <b>Please try again.</b>" +
        "<br /><br />" +
        "If you have already paid a deposit you will not be asked to make another payment.<br />" +
        "If this problem persists, please contact the practice.";
    }

    bootbox.alert({
      message: message,
      backdrop: true,
    });
  }

  #handleBookingTermsError(error) {
    console.error(error);

    if (error.message.includes("Booking session timed out")
      || error.message.includes("Booking terms must be accepted")) {
      this.#handleBookingSessionError(error.message);
      return;
    }

    let message;
    if (error.message === "Booking terms closed.") {
      message = "Booking terms declined. We cannot offer an appointment if the booking terms are not accepted.";
    } else if (error.message === "The server could not be reached.") {
      message = "The server could not be reached. Please try again later.";
    } else {
      message = "There was a problem with the appointment booking terms. Please try again.";
    }

    bootbox.alert({
      message,
      backdrop: true,
    });

    this.#scrollToContent();
  }

  #handleBeginBookingError(error) {
    console.error(error);

    if (error.message.includes("Booking session timed out")
      || error.message.includes("Booking terms must be accepted")) {
      this.#handleBookingSessionError(error.message);
      return;
    }

    let message;
    if (error.message === "The server could not be reached.") {
      message = "The server could not be reached. Please try again later.";
    } else {
      message = "There was a problem booking your appointment. Please try again.";
    }

    bootbox.alert({
      message,
      backdrop: true,
    });

    this.#scrollToContent();
  }

  #handleRequestDepositError(error) {
    console.error(error);

    if (error.message.includes("Booking session timed out")
      || error.message.includes("Booking terms must be accepted")) {
      this.#handleBookingSessionError(error.message);
      return;
    }

    let message;
    if (error.message.includes("maximum number of appointments booked")
      || error.message.includes("not currently accepting appointment bookings from you")) {
      message = error.message;
    } else if (error.message === "Payment cancelled.") {
      message = "The payment was cancelled, please try again.";
    } else if (error.message === "Login cancelled.") {
      message = "Login cancelled. Please log in or register to book your appointment.";
    } else if (error.message === "The server could not be reached.") {
      message = "The server could not be reached. Please try again later.";
    } else {
      message = "There was a problem retrieving the deposit payment details. Please try again.";
    }

    bootbox.alert({
      message,
      backdrop: true,
    });

    this.#scrollToContent();
  }

  #handleBookingSessionError(errMessage) {
    let displayMessage;
    if (errMessage.includes("Booking session timed out")) {
      displayMessage = "Your appointment booking timed out. <br> Please try again.";
    }
    else if (errMessage.includes("Booking terms must be accepted")) {
      displayMessage = "You must accept the booking terms before you can book an appointment."
    }
    else {
      displayMessage = errMessage;
    }

    bootbox.alert({
      message: displayMessage,
      callback: () => {
        window.pageRouter.refreshPage();
      },
      backdrop: true,
    });
  }
}
